import { FC, KeyboardEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'src/components';
import { LearnableValueKind } from 'src/api/learningSessions/types';
import {
  isCharacterLabel,
  isLiteralTranslationLabel,
  isRomanizationLabel,
} from 'src/api/settings/learningToggles';
import { romanizationTextAttrs } from 'src/utils/pinyinFont';
import { Alert } from 'src/apps/LearningSession/Common/Alert';
import { isKanaLabel } from 'src/apps/LearningSession/Common/learnableCards/PitchAccent/utils';
import type { PresentationCardData } from 'src/apps/LearningSession/Common/learnableCards/utils';
import { PronunciationPractice } from 'src/apps/LearningSession/Common/learnableCards/PresentationLearnableCard/PronunciationPractice';
import { ContinueControl } from '../ContinueControl';
import { AnimatedFlower } from '../LearnableWithFlower/AnimatedFlower';
import { BLOB_TRACKING_DATA } from '../tracking';
import { buildScriptFormCycle } from './scriptFormCycle';
import * as S from './styles';

const msgs = defineMessages({
  newPhrase: { id: 'blob_session_new_phrase', defaultMessage: 'New phrase' },
  addedFromSaved: { id: 'blob_session_added_from_saved', defaultMessage: 'Added from Saved' },
  yourAnswer: { id: 'learning_session_previous_incorrect_answer_title' },
  actuallyIWasRight: { id: 'learning_session_actually_i_was_right' },
});

type PresentationContentProps = {
  cardData: PresentationCardData;
  learnableId: number;
  languagePairId: number;
  growthLevel: number;
  showLiteralTranslation: boolean;
  showRomanization: boolean;
  onContinueClick: (event?: MouseEvent) => void;
  onContinueKeyUp: (event: KeyboardEvent) => void;
  onMarkPreviousAnswerCorrect: () => void;
  /** White text over a full-screen video, continuing with a swipe */
  overlay?: boolean;
  isFromSaved?: boolean;
};

export const PresentationContent: FC<PresentationContentProps> = ({
  cardData,
  learnableId,
  languagePairId,
  growthLevel,
  showLiteralTranslation,
  showRomanization,
  onContinueClick,
  onContinueKeyUp,
  onMarkPreviousAnswerCorrect,
  overlay = false,
  isFromSaved = false,
}) => {
  const intl = useIntl();
  const literalTranslationLabel = intl.formatMessage({ id: 'literal_translation' });
  const { item, definition, extraInfo, previousIncorrectAnswer, videoSrc } = cardData;

  const literalTranslation = showLiteralTranslation
    ? extraInfo.find(info => isLiteralTranslationLabel(info.label, literalTranslationLabel))
    : undefined;

  const otherInfo = useMemo(
    () =>
      extraInfo.filter(
        info =>
          info.kind === LearnableValueKind.TEXT &&
          !isLiteralTranslationLabel(info.label, literalTranslationLabel) &&
          (showRomanization || !isRomanizationLabel(info.label)) &&
          !(isCharacterLabel(info.label) && info.value === item.value),
      ),
    [extraInfo, item.value, literalTranslationLabel, showRomanization],
  );

  const romanization =
    cardData.romanization ?? extraInfo.find(info => isRomanizationLabel(info.label))?.value ?? null;
  const kana = cardData.kana ?? extraInfo.find(info => isKanaLabel(info.label))?.value ?? null;
  const nativeScript =
    cardData.nativeScript ?? extraInfo.find(info => isCharacterLabel(info.label))?.value ?? null;

  const scriptForms = useMemo(
    () =>
      item.kind === LearnableValueKind.TEXT
        ? buildScriptFormCycle({
            itemValue: item.value,
            nativeScript,
            kana,
            romanization,
          })
        : [item.value],
    [item.kind, item.value, kana, nativeScript, romanization],
  );

  const canCycleScriptForms = scriptForms.length > 1;
  const [scriptFormIndex, setScriptFormIndex] = useState(0);
  const formsKey = scriptForms.join('\0');

  useEffect(() => {
    setScriptFormIndex(0);
  }, [formsKey, learnableId]);

  const displayedItemValue = scriptForms[scriptFormIndex] ?? item.value;
  const showingRomanization = !!romanization && displayedItemValue === romanization;

  const cycleScriptForm = (event: MouseEvent) => {
    event.stopPropagation();
    setScriptFormIndex(index => (index + 1) % scriptForms.length);
  };

  return (
    <S.Content $overlay={overlay} data-testid="blob-presentation-content">
      {previousIncorrectAnswer && (
        <Alert level="danger" title={intl.formatMessage(msgs.yourAnswer)}>
          <S.PreviousAnswer>
            <span dir="auto">{previousIncorrectAnswer}</span>
            <Button buttonSize="small" buttonType="secondary" onClick={onMarkPreviousAnswerCorrect}>
              {intl.formatMessage(msgs.actuallyIWasRight)}
            </Button>
          </S.PreviousAnswer>
        </Alert>
      )}
      <S.HeaderFlower>
        <AnimatedFlower growthLevel={growthLevel} learnableId={learnableId} />
      </S.HeaderFlower>
      {isFromSaved ? (
        <S.SavedBadge data-testid="blob-saved-badge">
          <S.SavedIcon name="bookmark" />
          {intl.formatMessage(msgs.addedFromSaved)}
        </S.SavedBadge>
      ) : (
        !overlay && <S.Eyebrow>{intl.formatMessage(msgs.newPhrase)}</S.Eyebrow>
      )}
      {item.kind === LearnableValueKind.TEXT &&
        (canCycleScriptForms ? (
          <S.Item
            as="button"
            type="button"
            dir="auto"
            $showBigger={!!item.showBigger}
            $overlay={overlay}
            $clickable
            data-testid="blob-presentation-item"
            onClick={cycleScriptForm}
            {...(showingRomanization ? romanizationTextAttrs(displayedItemValue) : {})}
          >
            {displayedItemValue}
          </S.Item>
        ) : (
          <S.Item
            dir="auto"
            $showBigger={!!item.showBigger}
            $overlay={overlay}
            data-testid="blob-presentation-item"
          >
            {displayedItemValue}
          </S.Item>
        ))}
      {item.kind === LearnableValueKind.IMAGE && <S.Image src={item.value} alt="" />}
      {definition.kind === LearnableValueKind.TEXT && (
        <S.Definition $overlay={overlay}>
          <S.DefinitionValue dir="auto" $overlay={overlay}>
            {definition.value}
          </S.DefinitionValue>
          {literalTranslation && (
            <S.LiteralTranslation
              dir="auto"
              $overlay={overlay}
              data-testid="blob-literal-translation"
            >
              {' '}
              ({literalTranslation.value})
            </S.LiteralTranslation>
          )}
        </S.Definition>
      )}
      {definition.kind === LearnableValueKind.IMAGE && <S.Image src={definition.value} alt="" />}
      {otherInfo.length > 0 && (
        <S.ExtraInfo $overlay={overlay}>
          {otherInfo.map(info => (
            <S.ExtraInfoRow key={`${info.label}-${info.value}`} $overlay={overlay}>
              <S.ExtraInfoLabel $overlay={overlay}>{info.label}</S.ExtraInfoLabel>
              <span
                dir="auto"
                {...(isRomanizationLabel(info.label) ? romanizationTextAttrs(info.value) : {})}
              >
                {info.value}
              </span>
            </S.ExtraInfoRow>
          ))}
        </S.ExtraInfo>
      )}
      <S.Actions $overlay={overlay}>
        <PronunciationPractice
          key={learnableId}
          learnableId={learnableId}
          languagePairId={languagePairId}
          hasVideo={!!videoSrc}
          overlay={overlay}
          pill
          trackingData={BLOB_TRACKING_DATA}
        />
        <ContinueControl overlay={overlay} onClick={onContinueClick} onKeyUp={onContinueKeyUp} />
      </S.Actions>
    </S.Content>
  );
};
